import { useState, useEffect } from "react";
import axios from "axios";
import "./Settings.css";

const getInitials = (name, email) => {
  if (name) {
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return "U";
};

// Eye icon SVGs extracted to avoid repetition
const EyeIcon = ({ visible }) => visible ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

function ProfileManagement() {
  const cachedName  = localStorage.getItem("profile_name")  || "";
  const cachedEmail = localStorage.getItem("profile_email") || "";
  const cachedImage = localStorage.getItem("profile_image") || "";

  const [provider, setProvider]             = useState("");
  const [showOldPass, setShowOldPass]       = useState(false);
  const [showNewPass, setShowNewPass]       = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [profileImage, setProfileImage]     = useState(cachedImage);
  const [stagedImageFile, setStagedImageFile] = useState(null);
  const [dummyPhone, setDummyPhone]         = useState("");

  const [formData, setFormData]     = useState({ fullName: cachedName, email: cachedEmail });
  const [initialData, setInitialData] = useState({ fullName: cachedName, email: cachedEmail, profile_image: cachedImage });
  const [passwordData, setPasswordData] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  const fallbackAvatar = `https://ui-avatars.com/api/?name=${getInitials(formData.fullName, formData.email)}&size=150&background=4f46e5&color=fff&bold=true`;

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res  = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      const profile = { fullName: data.name, email: data.email };
      setFormData(profile);
      setInitialData({ ...profile, profile_image: data.profile_image });
      setProfileImage(data.profile_image);
      setProvider(data.provider);
      localStorage.setItem("profile_name",  data.name);
      localStorage.setItem("profile_email", data.email);
      localStorage.setItem("profile_image", data.profile_image);
    } catch (err) {
      console.log(err);
      showNotification("Session expired. Please login again.", "error");
    }
  };

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGlobalSave = async () => {
    let profileSaved = false, passwordSaved = false, imageSaved = false;

    if (formData.fullName !== initialData.fullName) {
      try {
        const token = localStorage.getItem("token");
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/profile`, { name: formData.fullName, email: formData.email }, { headers: { Authorization: `Bearer ${token}` } });
        setInitialData((prev) => ({ ...prev, fullName: formData.fullName }));
        localStorage.setItem("profile_name", formData.fullName);
        profileSaved = true;
      } catch { showNotification("Profile update failed", "error"); return; }
    }

    if (stagedImageFile) {
      try {
        const token = localStorage.getItem("token");
        const form  = new FormData();
        form.append("image", stagedImageFile);
        const res      = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/profile/upload`, form, { headers: { Authorization: `Bearer ${token}` } });
        const newImage = res.data.profile_image;
        setProfileImage(newImage);
        setInitialData((prev) => ({ ...prev, profile_image: newImage }));
        localStorage.setItem("profile_image", newImage);
        window.dispatchEvent(new Event("profileImageUpdated"));
        setStagedImageFile(null);
        imageSaved = true;
      } catch { showNotification("Profile picture upload failed", "error"); return; }
    }

    if (passwordData.oldPassword || passwordData.newPassword || passwordData.confirmPassword) {
      if (!passwordData.oldPassword)  { showNotification("You must enter your current password to set a new one.", "error"); return; }
      if (!passwordData.newPassword)  { showNotification("Please enter your new password.", "error"); return; }
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
      if (!passwordRegex.test(passwordData.newPassword)) { showNotification("Password must be 8+ chars with 1 uppercase, 1 lowercase, 1 number, and 1 special character.", "error"); return; }
      if (passwordData.newPassword !== passwordData.confirmPassword) { showNotification("New passwords do not match.", "error"); return; }
      try {
        const token = localStorage.getItem("token");
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/change-password`, passwordData, { headers: { Authorization: `Bearer ${token}` } });
        setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
        passwordSaved = true;
      } catch (err) { showNotification(err.response?.data?.message || "Password update failed. Make sure your current password is correct.", "error"); return; }
    }

    if (profileSaved || passwordSaved || imageSaved) {
      showNotification("Changes saved successfully!");
    } else {
      showNotification("No changes were made to save.", "error");
    }
  };

  const handleCancel = () => {
    setFormData({ fullName: initialData.fullName, email: initialData.email });
    setProfileImage(initialData.profile_image);
    setStagedImageFile(null);
    setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    setDummyPhone("");
  };

  return (
    <>
      {notification.show && (
        <div className={`notification-toast ${notification.type === "success" ? "toast-success" : "toast-error"}`}>
          {notification.message}
        </div>
      )}

      <div className="gs-card">

        {/* ── TOP: Avatar + Title inline ── */}
        <div className="gs-top-row">
          <div className="gs-avatar-wrap">
            <img
              src={provider === "google" ? profileImage : (profileImage || fallbackAvatar)}
              className="gs-avatar-img"
              alt="Profile"
            />
          </div>
          <div className="gs-top-title-block">
            <h2 className="gs-card-section-title">General Settings</h2>
            {provider === "local" && (
              <>
                <input
                  type="file"
                  hidden
                  id="uploadAvatar"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setStagedImageFile(file);
                      setProfileImage(URL.createObjectURL(file));
                    }
                  }}
                />
                <label htmlFor="uploadAvatar" className="gs-change-photo-btn">
                  Change Photo
                </label>
              </>
            )}
          </div>
        </div>

        {/* ── Row 1: Full Name + Phone Number ── */}
      
<div className="gs-form-row" style={{ marginTop: '16px' }}>
          <div className="gs-form-group">
            <label className="gs-label">FULL NAME</label>
            <input type="text" className="gs-input" name="fullName" value={formData.fullName} onChange={handleChange} />
          </div>
          <div className="gs-form-group">
            <label className="gs-label">PHONE NUMBER</label>
            <input
              type="tel"
              name="dummy-phone"
              autoComplete="off"
              className="gs-input"
              value={dummyPhone}
              maxLength={11}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 11) setDummyPhone(value);
              }}
            />
          </div>
        </div>

        {/* ── Row 2: Email + Organization ── */}
        <div className="gs-form-row">
          <div className="gs-form-group">
            <label className="gs-label">EMAIL</label>
            <input type="email" className="gs-input gs-input-disabled" name="email" value={formData.email} disabled />
          </div>
          <div className="gs-form-group">
            <label className="gs-label">ORGANIZATION</label>
            <input type="text" className="gs-input gs-input-disabled" placeholder="Your Company" disabled />
          </div>
        </div>

        {/* ── Password & Security (local users only) ── */}
        {provider === "local" && (
          <>
            <h2 className="gs-card-section-title gs-section-title-spaced">Password & Security</h2>

            {/* Current + New side by side */}
<div className="gs-form-row" style={{ marginTop: '16px' }}>
                <div className="gs-form-group">
                <label className="gs-label">CURRENT PASSWORD</label>
                <div className="gs-input-wrap">
                  <input type={showOldPass ? "text" : "password"} autoComplete="new-password" className="gs-input" value={passwordData.oldPassword} onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })} />
                  <button type="button" className="gs-eye-btn" onClick={() => setShowOldPass(!showOldPass)}><EyeIcon visible={showOldPass} /></button>
                </div>
              </div>
              <div className="gs-form-group">
                <label className="gs-label">NEW PASSWORD</label>
                <div className="gs-input-wrap">
                  <input type={showNewPass ? "text" : "password"} autoComplete="new-password" className="gs-input" placeholder="Min. 8 characters,1 number ,1 special" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} />
                  <button type="button" className="gs-eye-btn" onClick={() => setShowNewPass(!showNewPass)}><EyeIcon visible={showNewPass} /></button>
                </div>
              </div>
            </div>

            {/* Confirm — half width */}
            <div className="gs-form-row">
              <div className="gs-form-group">
                <label className="gs-label">CONFIRM NEW PASSWORD</label>
                <div className="gs-input-wrap">
                  <input type={showConfirmPass ? "text" : "password"} autoComplete="new-password" className="gs-input" placeholder="Match new password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} />
                  <button type="button" className="gs-eye-btn" onClick={() => setShowConfirmPass(!showConfirmPass)}><EyeIcon visible={showConfirmPass} /></button>
                </div>
              </div>
              <div className="gs-form-group gs-form-group-empty" />
            </div>
          </>
        )}

        {/* ── Footer ── */}
        <div className="gs-footer-actions">
          <button className="gs-btn-save" onClick={handleGlobalSave}>Save Changes</button>
        </div>

      </div>
    </>
  );
}

export default ProfileManagement;